import React, { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useUserContext } from './UserProvider'
import { Button } from '../designsystem/Button'
import { Card } from '../designsystem/Card'
import { Toggle } from '../designsystem/Toggle'
import { Marketing } from '../designsystem/Text'

interface CookiePreferences {
  necessary: boolean
  analytics: boolean
  marketing: boolean
}

interface CookieConsentContextType {
  preferences: CookiePreferences | null
  showBanner: boolean
  acceptAll: () => void
  rejectAll: () => void
  updatePreferences: (prefs: CookiePreferences) => void
  openPreferences: () => void
}

const CookieConsentContext =
  React.createContext<CookieConsentContextType | null>(null)

const COOKIE_CONSENT_KEY = 'stellate-cookie-consent'
const COOKIE_EXPIRY_DAYS = 365

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
}

declare global {
  interface Window {
    gtag?: (...args: any[]) => void
    dataLayer?: any[]
  }
}

export const useCookieConsent = () => {
  const context = React.useContext(CookieConsentContext)
  if (!context) {
    throw new Error(
      'useCookieConsent must be used within CookieConsentProvider',
    )
  }
  return context
}

const updateGoogleConsentMode = (preferences: CookiePreferences) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      ad_storage: preferences.marketing ? 'granted' : 'denied',
      analytics_storage: preferences.analytics ? 'granted' : 'denied',
      functionality_storage: 'granted',
      personalization_storage: preferences.marketing ? 'granted' : 'denied',
      security_storage: 'granted',
    })

    // Also trigger GTM events to notify about consent changes
    window.gtag('event', 'cookie_consent_update', {
      analytics_storage: preferences.analytics ? 'granted' : 'denied',
      ad_storage: preferences.marketing ? 'granted' : 'denied',
    })
  }
}

const saveCookiePreferences = (preferences: CookiePreferences) => {
  const expiryDate = new Date()
  expiryDate.setTime(
    expiryDate.getTime() + COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
  )
  document.cookie = `${COOKIE_CONSENT_KEY}=${JSON.stringify(
    preferences,
  )}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`
  updateGoogleConsentMode(preferences)
}

const getCookiePreferences = (): CookiePreferences | null => {
  if (typeof document === 'undefined') return null

  const cookies = document.cookie.split(';')
  const consentCookie = cookies.find((cookie) =>
    cookie.trim().startsWith(`${COOKIE_CONSENT_KEY}=`),
  )

  if (consentCookie) {
    try {
      const value = consentCookie.split('=')[1]
      return JSON.parse(decodeURIComponent(value))
    } catch {
      return null
    }
  }

  return null
}

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const router = useRouter()
  const ctx = useUserContext()
  const [preferences, setPreferences] = useState<CookiePreferences | null>(null)
  const [showBanner, setShowBanner] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const isImpersonated = Boolean(ctx?.user?.impersonator)

  useEffect(() => {
    const savedPreferences = getCookiePreferences()
    if (savedPreferences) {
      setPreferences(savedPreferences)
      updateGoogleConsentMode(savedPreferences)
    } else {
      setShowBanner(true)
    }
  }, [])

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    }
    setPreferences(allAccepted)
    saveCookiePreferences(allAccepted)
    setShowBanner(false)
    setShowPreferences(false)
  }

  const rejectAll = () => {
    setPreferences(defaultPreferences)
    saveCookiePreferences(defaultPreferences)
    setShowBanner(false)
    setShowPreferences(false)
  }

  const updatePreferences = (prefs: CookiePreferences) => {
    setPreferences(prefs)
    saveCookiePreferences(prefs)
    setShowBanner(false)
    setShowPreferences(false)
  }

  const openPreferences = () => {
    setShowPreferences(true)
    setShowBanner(true) // Always enable banner container for modal to show
  }

  // Don't show banner for impersonated users or login callbacks
  const shouldShowComponent =
    showBanner &&
    !isImpersonated &&
    !router.asPath.startsWith('/login/callback')

  // Show banner part only for users without preferences, unless preferences modal is open
  const shouldShowBannerContent = !preferences || showPreferences

  return (
    <CookieConsentContext.Provider
      value={{
        preferences,
        showBanner: shouldShowComponent && shouldShowBannerContent,
        acceptAll,
        rejectAll,
        updatePreferences,
        openPreferences,
      }}
    >
      {children}
      {shouldShowComponent && (
        <CookieBanner
          showPreferences={showPreferences}
          showBannerContent={shouldShowBannerContent}
          onShowPreferences={() => setShowPreferences(true)}
          onHidePreferences={() => {
            setShowPreferences(false)
            setShowBanner(false)
          }}
        />
      )}
    </CookieConsentContext.Provider>
  )
}

const CookieBanner: React.FC<{
  showPreferences: boolean
  showBannerContent: boolean
  onShowPreferences: () => void
  onHidePreferences: () => void
}> = ({
  showPreferences,
  showBannerContent,
  onShowPreferences,
  onHidePreferences,
}) => {
  const { acceptAll, rejectAll, updatePreferences, preferences } =
    useCookieConsent()
  const [localPreferences, setLocalPreferences] = useState<CookiePreferences>(
    preferences || defaultPreferences,
  )

  if (showPreferences) {
    return (
      <div className='fixed inset-0 bg-neutral bg-opacity-50 flex items-center justify-center z-50 p-6 cookie-modal'>
        <div className='bg-white rounded-12 shadow-big max-w-xl w-full p-8 max-h-[85vh] overflow-y-auto marketing-page'>
          <div className='flex items-center justify-between mb-8'>
            <h5 className='text-29 font-demibold text-neutral'>
              Cookie Settings
            </h5>
            <button
              onClick={onHidePreferences}
              className='min-clickable-area p-2 hover:bg-neutral-7 rounded-8 transition-colors text-neutral-60 hover:text-neutral'
              aria-label='Close cookie preferences'
            >
              <svg
                className='w-6 h-6'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
          </div>

          <div className='space-y-8'>
            <div className='flex items-start gap-6 py-6 border-b border-neutral-15'>
              <div className='flex-1'>
                <h6 className='font-demibold text-16 text-neutral mb-2'>
                  Necessary Cookies
                </h6>
                <p className='text-15 text-neutral-60 leading-1.5'>
                  Essential cookies required for the website to function
                  properly. These cannot be disabled.
                </p>
              </div>
              <div className='flex items-center justify-center w-12 h-6 bg-blue rounded-12 relative flex-shrink-0'>
                <div className='w-4 h-4 bg-white rounded-full transform translate-x-3 transition-transform'></div>
                <span className='sr-only'>Always enabled</span>
              </div>
            </div>

            <div className='flex items-start gap-6 py-6 border-b border-neutral-15'>
              <div className='flex-1'>
                <h6 className='font-demibold text-18 text-neutral mb-2'>
                  Analytics Cookies
                </h6>
                <p className='text-15 text-neutral-60 leading-1.5'>
                  Help us understand how visitors interact with our website by
                  collecting anonymous usage data.
                </p>
              </div>
              <button
                onClick={() =>
                  setLocalPreferences((prev) => ({
                    ...prev,
                    analytics: !prev.analytics,
                  }))
                }
                className={`flex items-center justify-center w-12 h-6 rounded-12 transition-colors focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2 flex-shrink-0 ${
                  localPreferences.analytics ? 'bg-blue' : 'bg-neutral-25'
                }`}
                aria-label={`${
                  localPreferences.analytics ? 'Disable' : 'Enable'
                } analytics cookies`}
              >
                <div
                  className={`w-4 h-4 bg-white rounded-full transition-transform ${
                    localPreferences.analytics
                      ? 'translate-x-3'
                      : '-translate-x-3'
                  }`}
                ></div>
              </button>
            </div>

            <div className='flex items-start gap-6 py-6'>
              <div className='flex-1'>
                <h6 className='font-demibold text-18 text-neutral mb-2'>
                  Marketing Cookies
                </h6>
                <p className='text-15 text-neutral-60 leading-1.5'>
                  Used to deliver personalized advertisements and measure
                  campaign effectiveness.
                </p>
              </div>
              <button
                onClick={() =>
                  setLocalPreferences((prev) => ({
                    ...prev,
                    marketing: !prev.marketing,
                  }))
                }
                className={`flex items-center justify-center w-12 h-6 rounded-12 transition-colors focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2 flex-shrink-0 ${
                  localPreferences.marketing ? 'bg-blue' : 'bg-neutral-25'
                }`}
                aria-label={`${
                  localPreferences.marketing ? 'Disable' : 'Enable'
                } marketing cookies`}
              >
                <div
                  className={`w-4 h-4 bg-white rounded-full transition-transform ${
                    localPreferences.marketing
                      ? 'translate-x-3'
                      : '-translate-x-3'
                  }`}
                ></div>
              </button>
            </div>
          </div>

          <div className='flex gap-4 mt-10'>
            <button
              onClick={() => updatePreferences(localPreferences)}
              className='font-demibold flex-1 inline-flex items-center justify-center h-12 px-6 rounded-8 text-15 leading-none bg-blue text-white hover:bg-blue-110 transition-colors focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2'
            >
              Save Preferences
            </button>
            <button
              onClick={onHidePreferences}
              className='inline-flex text-white items-center justify-center h-12 px-6 rounded-8 font-demibold bg-red text-15 leading-none hover:bg-red-110 transition-colors focus:outline-none focus:ring-2 focus:ring-neutral focus:ring-offset-2'
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {showBannerContent && (
        <div className='fixed bottom-6 left-6 right-6 bg-white rounded-12 shadow-big border border-neutral-15 p-8 z-40 max-w-2xl mx-auto cookie-banner marketing-page'>
          <div className='flex items-start gap-6'>
            <div className='flex-1 min-w-0'>
              <h5 className='font-demibold text-neutral text-14 mb-3'>
                Cookie Preferences
              </h5>
              <p className='text-15 text-neutral-60 leading-1.5 mb-6'>
                We use cookies to enhance your browsing experience, serve
                personalized content, and analyze our traffic. You can customize
                your preferences or accept all cookies to continue.
              </p>

              <div className='flex gap-3 flex-wrap'>
                <button
                  onClick={acceptAll}
                  className='inline-flex items-center h-10 px-4 rounded-8 font-demibold text-15 leading-none bg-blue text-white hover:bg-blue-110 transition-colors focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2'
                >
                  Accept All
                </button>
                <button
                  onClick={rejectAll}
                  className='inline-flex items-center h-10 px-4 rounded-8 font-demibold text-15 leading-none bg-red text-white hover:bg-red-110 transition-colors focus:outline-none focus:ring-2 focus:ring-red focus:ring-offset-2'
                >
                  Reject All
                </button>
                <button
                  onClick={onShowPreferences}
                  className='inline-flex items-center h-10 px-4 rounded-8 font-demibold text-15 leading-none border border-neutral-25 text-neutral hover:bg-neutral-7 transition-colors focus:outline-none focus:ring-2 focus:ring-neutral focus:ring-offset-2'
                >
                  Customize
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
